/*
 * The invitation page's hand-overs (VIS-332, VIS-441): to Auth0 sign-in or
 * sign-out, and to the Auth0 set-password page. Auth0 may return to the app's
 * origin with no app state, so the page records where it was and why before
 * it leaves. On the way back the app reads the record once and returns to the
 * invitation. The record lives in this tab only and expires, so an abandoned
 * hand-over never redirects a later, unrelated visit.
 */

export const HAND_OVER_KEY = 'visitone.invitationHandOver'
export const HAND_OVER_TTL_MS = 30 * 60 * 1000

export type HandOverMode = 'signIn' | 'setPassword' | 'signOut'
/** `accountReady`: a sign-in that started right after the password was set. */
export type HandOver = { path: string; mode: HandOverMode; accountReady?: true }

const MODES: HandOverMode[] = ['signIn', 'setPassword', 'signOut']
const INVITATION_PATH = /^\/invitation\/[^/?#]+$/

export function recordHandOver(handOver: HandOver, now = Date.now()) {
  try {
    sessionStorage.setItem(HAND_OVER_KEY, JSON.stringify({ ...handOver, at: now }))
  } catch {
    // Storage can be unavailable; the return then lands on the app as usual.
  }
}

function read(): (HandOver & { at: number }) | null {
  try {
    const raw = sessionStorage.getItem(HAND_OVER_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<HandOver & { at: number }>
    if (typeof value.path !== 'string' || !INVITATION_PATH.test(value.path)) return null
    if (!value.mode || !MODES.includes(value.mode) || typeof value.at !== 'number') return null
    const accountReady = value.mode === 'signIn' && value.accountReady === true ? { accountReady: true as const } : {}
    return { path: value.path, mode: value.mode, at: value.at, ...accountReady }
  } catch {
    return null
  }
}

/** Whether a hand-over is waiting to be returned to, without using it up. */
export function hasHandOver(now = Date.now()): boolean {
  const value = read()
  return value !== null && now - value.at <= HAND_OVER_TTL_MS && value.at <= now
}

/** The waiting hand-over, used up: it is removed whether or not it is still valid. */
export function takeHandOver(now = Date.now()): HandOver | null {
  const value = read()
  try {
    sessionStorage.removeItem(HAND_OVER_KEY)
  } catch {
    // Nothing to remove.
  }
  if (!value || now - value.at > HAND_OVER_TTL_MS || value.at > now) return null
  const { at: _at, ...handOver } = value
  return handOver
}
